import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiInfo, FiMapPin } from 'react-icons/fi';
import { cn } from '../utils/cn';

const Arrow = ({ x, y, rot = 0 }) => (
  <polygon 
    points="0,0 12,-7 12,7" 
    fill="#eab308" 
    transform={`translate(${x}, ${y}) rotate(${rot})`} 
  />
);

export default function InteractiveMap({ onSelectHall }) {
  const [hoveredHall, setHoveredHall] = useState(null);

  const mapData = [
    { id: 'G', points: "180,60 360,60 360,200 180,200", textX: 270, textY: 130, fill: "#a5a5a5", name: "Produktion G", unavailable: true },
    { id: 'F', points: "360,150 450,150 450,300 360,300", textX: 405, textY: 225, fill: "#9bb0b8", name: "Lager F", unavailable: true },
    { id: 'E', points: "180,200 360,200 360,300 180,300", textX: 270, textY: 250, fill: "#8db0c5", name: "Lager E", unavailable: true },
    { id: 'D', points: "180,300 450,300 450,420 180,420", textX: 315, textY: 360, fill: "#abaaa9", name: "Halle D", unavailable: true },
    { id: 'C', points: "170,420 450,420 450,560 175,560 175,520 158,520 158,465 170,465", textX: 315, textY: 490, fill: "#94aeb5", name: "Produktion C" },
    { id: 'B', points: "140,520 175,520 175,560 238,560 238,640 140,640", textX: 189, textY: 580, fill: "#8faecc", name: "Gebäude B", unavailable: true },
    { id: 'A', points: "158,640 238,640 242,750 162,750", textX: 200, textY: 695, fill: "#a5a5a5", name: "Gebäude A", unavailable: true }
  ];

  const arrows = [
    { x: 375, y: 95, rot: 0 },
    { x: 375, y: 115, rot: 0 },
    { x: 400, y: 135, rot: -90 },
    { x: 415, y: 135, rot: -90 },
    { x: 465, y: 260, rot: 0 },
    { x: 465, y: 310, rot: 0 },
    { x: 465, y: 330, rot: 0 },
    { x: 465, y: 390, rot: 0 },
    { x: 310, y: 575, rot: 90 },
    { x: 325, y: 575, rot: 90 },
    { x: 415, y: 575, rot: 90 },
    { x: 260, y: 580, rot: 0 },
    { x: 255, y: 730, rot: 0 },
    { x: 125, y: 590, rot: 180 },
  ];

  return (
    <div className="relative w-full p-2 mx-auto max-w-4xl overflow-hidden rounded-lg shadow-lg border border-gray-200 bg-white">
      <div className="absolute top-6 left-6 z-10 flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-white/95 backdrop-blur-sm rounded-sm shadow-md text-emerald-950 border border-gray-100">
        <SafeIcon icon={FiMapPin} className="text-yellow-500" />
        <span>Lageplan Heinsberg</span>
      </div>
      
      <div className="absolute top-6 right-6 z-10 flex items-center gap-2 px-4 py-2 text-xs font-medium bg-emerald-900/90 text-white backdrop-blur-sm rounded-sm shadow-md">
        <SafeIcon icon={FiInfo} className="text-yellow-400" />
        <span>Halle anklicken für Details</span>
      </div>

      <div className="w-full h-auto bg-[#788275] rounded-sm overflow-hidden mt-16 md:mt-0">
        <svg 
          viewBox="0 0 500 800" 
          className="w-full h-full drop-shadow-xl"
          preserveAspectRatio="xMidYMid meet"
        >
          {arrows.map((arr, idx) => (
            <Arrow key={`arrow-${idx}`} x={arr.x} y={arr.y} rot={arr.rot} />
          ))}

          {mapData.map((area) => (
            <motion.g
              key={area.id}
              onMouseEnter={() => setHoveredHall(area.id)}
              onMouseLeave={() => setHoveredHall(null)}
              onClick={() => onSelectHall(area.id)}
              className={cn(
                "transition-all cursor-pointer",
                area.unavailable ? "opacity-60" : "hover:brightness-110"
              )}
              style={{ transformOrigin: `${area.textX}px ${area.textY}px` }}
            >
              <polygon 
                points={area.points} 
                fill={area.unavailable ? "#4a4a4a" : area.fill}
                className={cn(
                  "transition-all duration-300",
                  hoveredHall === area.id ? "stroke-yellow-400 stroke-[4px]" : "stroke-[#333333] stroke-[2px]"
                )}
              />
              <text 
                x={area.textX} 
                y={area.textY} 
                fill={area.unavailable ? "#999" : "white"}
                fontSize="38" 
                fontWeight="bold" 
                fontFamily="sans-serif"
                textAnchor="middle" 
                dominantBaseline="middle"
                className="pointer-events-none drop-shadow-md"
              >
                {area.id}
              </text>
              {area.unavailable && hoveredHall === area.id && (
                <text 
                  x={area.textX} 
                  y={area.textY + 30} 
                  fill="#ffffff" 
                  fontSize="12" 
                  fontWeight="600" 
                  textAnchor="middle" 
                  className="pointer-events-none"
                >
                  Vermietet
                </text>
              )}
            </motion.g>
          ))}

          <g className="legend" transform="translate(360, 640)">
            <rect x="0" y="0" width="130" height="70" fill="transparent" stroke="#eab308" strokeWidth="1.5" />
            <Arrow x={20} y={20} rot={0} />
            <text x="35" y="25" fill="#eab308" fontSize="14" fontFamily="sans-serif" fontWeight="500">
              Eingänge
            </text>
            <Arrow x={12} y={50} rot={0} />
            <Arrow x={24} y={50} rot={0} />
            <text x="35" y="55" fill="#eab308" fontSize="14" fontFamily="sans-serif" fontWeight="500">
              Toreinfahrten
            </text>
          </g>
        </svg>
      </div>
    </div>
  );
}